import {
  MenuItem,
  ListItemIcon,
  MenuItemProps,
  SvgIcon,
  ListItemText,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import Button, { IconButtonProps } from '@ui/Button';
import { ProjectFragment } from 'components/fragments.generated';

const useStyles = makeStyles()(theme => ({
  menuItemIcon: {
    minWidth: theme.spacing(4),
  },
}));

/**
 * @description An StudioClip action component that is rendered as an inline `IconButton` on desktop
 * devices.
 */
const StudioClipActionDesktop = React.forwardRef<
  HTMLButtonElement,
  IconButtonProps
>((props, ref) => {
  const { title, ...rest } = props;
  return <Button ref={ref} {...rest} variant="icon" />;
});

interface StudioClipActionMobileProps extends Omit<MenuItemProps, 'button'> {
  menuItemText: string;
  children: React.ReactElement<typeof SvgIcon>;
  // Use following props for anchor tag scenario
  component?: React.ElementType;
  href?: string;
  download?: string;
}

/**
 * @description An StudioClip action component that is rendered within a `Menu` on a mobile device.
 * This is a small wrapper around the `MenuItem` component.
 */
const StudioClipActionMobile = React.forwardRef<
  HTMLLIElement,
  StudioClipActionMobileProps
>((props, ref) => {
  const { menuItemText, children } = props;
  const { classes } = useStyles();
  return (
    <MenuItem ref={ref} {...props}>
      <ListItemIcon className={classes.menuItemIcon}>{children}</ListItemIcon>
      <ListItemText>{menuItemText}</ListItemText>
    </MenuItem>
  );
});

export type StudioClipActionProps = Omit<
  IconButtonProps & MenuItemProps,
  'children' | 'aria-label' | 'onClick'
> & {
  onClick: NonNullable<IconButtonProps['onClick'] & MenuItemProps['onClick']>;
  setDownloadError?: (downloadError: string) => void;
  project: ProjectFragment;
};

const StudioClipAction = {
  Mobile: StudioClipActionMobile,
  Desktop: StudioClipActionDesktop,
};

export default StudioClipAction;
